
import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Button,
  Form,
  InputGroup,
  Badge,
  Tab,
  Nav,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import DataApi from "../../api/dataApi";
import PubSub from "pubsub-js";
import ResizableTable from "../common/ResizableTable";
import BulkIssue from "./BulkIssue";
import { convertToUserTimezone } from "../../utils/convertTimeZone";
import { useTimeZone } from "../../contexts/TimeZoneContext";
import moment from "moment";
import PermissionDenied from "../../utils/permission_denied";


const BookIssue = ({ permissions }) => {
  const navigate = useNavigate();
  const { timeZone } = useTimeZone();
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedLibraryCard, setSelectedLibraryCard] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    book_id: "",
    card_id: "",
    issued_to: "",
    issue_date: new Date().toISOString().split("T")[0],
    due_date: "",
    condition_before: "Good",
    remarks: "",
  });
  const [issuedBooks, setIssuedBooks] = useState([]);
  const [loadingIssuedBooks, setLoadingIssuedBooks] = useState(false);
  const [activeTab, setActiveTab] = useState("issue");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;
  const [durationDays, setDurationDays] = useState(7);
  const [bookBankMode, setBookBankMode] = useState(false);
  const [company, setCompany] = useState(false);
  const [companybookbank, setCompanybookbank] = useState(false);
  const [bookBankLimit, setBookBankLimit] = useState(false);
  const [librarySettings, setLibrarySettings] = useState(null);

  const bookInputRef = useRef(null);
  const bookSearchInputRef = useRef(null);
  const bookInputTimer = useRef(null);
  const cardInputTimer = useRef(null);

  const [back, setBack] = useState(false);

  const allowEdit = permissions?.allowEdit;

  console.log("Book Issue Permissions:", permissions, allowEdit);

  useEffect(() => {
    FetchCompany();
    fetchIssuedBooks();
    fetchLibrarySettings();

    setTimeout(() => {
      const bookSelect = bookInputRef.current?.querySelector("input");
      if (bookSelect) {
        bookSelect.focus();
        bookSelect.click();
      }
    }, 300);


    const token = PubSub.subscribe("OPEN_ADD_BOOK_ISSUE_MODAL", () => {
      resetForm();
      setActiveTab("issue");
      setTimeout(() => {
        const bookSelect = bookInputRef.current?.querySelector("input");
        if (bookSelect) {
          bookSelect.focus();
          bookSelect.click();
        }
      }, 100);
    });

    return () => {
      PubSub.unsubscribe(token);
      if (bookInputTimer.current) clearTimeout(bookInputTimer.current);
      if (cardInputTimer.current) clearTimeout(cardInputTimer.current);
    };
  }, []);


  const loadLibrarySettings = async () => {
    try {
      const api = new DataApi("librarysettings");
      const resp = await api.get("/all");
      console.log("Library settings response:", resp);
      let settings = {};
      if (resp.data && resp.data.success) {
        settings = resp.data.data || {};
      } else if (resp.data) {
        settings = resp.data;
      }
      // if array wrap
      if (Array.isArray(settings)) settings = settings[0] || {};
      setLibrarySettings(settings);
    } catch (e) {
      console.error("Error loading library settings:", e);
    }
  };

  useEffect(() => {
    loadLibrarySettings();
  }, []);

  const callNumberLabel = librarySettings?.call_number_label || "Call Number";
  console.log("librarySettings", librarySettings);

  const fetchLibrarySettings = async () => {
    try {
      const settingsApi = new DataApi("librarysettings");
      const response = await settingsApi.get("/all");

      if (response.data && response.data.success && response.data.data) {
        // Handle response structure
      } else if (
        response.data &&
        typeof response.data === "object" &&
        !Array.isArray(response.data)
      ) {
        const duration = parseInt(response.data.duration_days) || 7;
        setDurationDays(duration);
      }
    } catch (error) {
      console.error("Error fetching library settings:", error);
    }
  };


  useEffect(() => {
    if (!formData.due_date && durationDays) {
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + durationDays);
      setFormData((prev) => ({
        ...prev,
        due_date: dueDate.toISOString().split("T")[0],
      }));
    }
  }, [durationDays]);


  useEffect(() => {
    if (selectedLibraryCard && selectedLibraryCard.data) {
      setFormData((prev) => ({
        ...prev,
        card_id: selectedLibraryCard.data.id,
        issued_to:
          selectedLibraryCard.data.user_id ||
          selectedLibraryCard.data.student_id ||
          "",
      }));
      setSelectedUser(null);
    } else if (selectedUser && selectedUser.data) {
      setFormData((prev) => ({
        ...prev,
        issued_to: selectedUser.data.id,
        card_id: "",
      }));
      setSelectedLibraryCard(null);
    }
  }, [selectedLibraryCard, selectedUser]);


  useEffect(() => {
    if (selectedBook && selectedBook.data) {
      setFormData((prev) => ({
        ...prev,
        book_id: selectedBook.data.id,
      }));
    }
  }, [selectedBook]);


  const fetchIssuedBooks = async () => {
    try {
      setLoadingIssuedBooks(true);
      const issueApi = new DataApi("bookissue");
      const response = await issueApi.fetchAll();
      console.log("response->>>", response);

      if (response.data && Array.isArray(response.data)) {
        const activeIssues = response.data.filter(
          (issue) =>
            issue.status === "issued" ||
            issue.status === null ||
            issue.status === undefined ||
            issue.return_date === null
        );
        setIssuedBooks(activeIssues);
      }
    } catch (error) {
      console.error("Error fetching issued books:", error);
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Error",
        message: "Failed to fetch issued books",
      });
    } finally {
      setLoadingIssuedBooks(false);
    }
  };

  const resetForm = () => {
    setSelectedBook(null);
    setSelectedLibraryCard(null);
    setSelectedUser(null);
    setFormData({
      book_id: "",
      card_id: "",
      issued_to: "",
      issue_date: new Date().toISOString().split("T")[0],
      due_date: (() => {
        const dueDate = new Date();
        dueDate.setDate(dueDate.getDate() + durationDays);
        return dueDate.toISOString().split("T")[0];
      })(),
      condition_before: "Good",
      remarks: "",
    });

    setTimeout(() => {
      if (bookSearchInputRef.current) {
        bookSearchInputRef.current.focus();
      }
    }, 200);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      return dateString;
    }
  };

  // Fixed filteredIssuedBooks - now filters based on bookBankMode toggle
  // const filteredIssuedBooks = issuedBooks.filter((issue) => {
  //   // First filter by book bank mode based on toggle
  //   if (bookBankMode) {
  //     // If toggle is ON - show only records with is_book_bank = true
  //     if (issue.is_book_bank !== true) return false;
  //   } else {
  //     // If toggle is OFF - show only records with is_book_bank = false
  //     if (issue.is_book_bank === true) return false;
  //   }

  //   // Then apply search filter
  //   if (!searchTerm) return true;

  //   const query = searchTerm.toLowerCase();
  //   const bookTitle = (issue.book_title || "").toLowerCase();
  //   const isbn = (issue.book_isbn || "").toLowerCase();
  //   const userName = (
  //     issue.issued_to_name ||
  //     issue.student_name ||
  //     issue.issued_to ||
  //     ""
  //   ).toLowerCase();
  //   const cardNumber = (issue.card_number || "").toLowerCase();

  //   return (
  //     bookTitle.includes(query) ||
  //     isbn.includes(query) ||
  //     userName.includes(query) ||
  //     cardNumber.includes(query)
  //   );
  // });

  const filteredIssuedBooks = issuedBooks.filter((issue) => {
    // First filter by book bank mode based on toggle
    if (bookBankMode) {
      // If toggle is ON - show only records with is_book_bank = true
      // Convert to boolean to handle 0/1, true/false, null/undefined consistently
      const isBookBank = issue.is_book_bank === true || issue.is_book_bank === 1;

      if (!isBookBank) return false;
    } else {
      // If toggle is OFF - show records with is_book_bank = false, null, undefined, or 0
      const isBookBank = issue.is_book_bank === true || issue.is_book_bank === 1;
      if (isBookBank) return false;
    }

    // Then apply search filter
    if (!searchTerm) return true;

    const query = searchTerm.toLowerCase();
    const bookTitle = (issue.book_title || "").toLowerCase();
    const isbn = (issue.book_isbn || "").toLowerCase();
    const userName = (
      issue.issued_to_name ||
      issue.student_name ||
      issue.issued_to ||
      ""
    ).toLowerCase();
    const cardNumber = (issue.card_number || "").toLowerCase();

    return (
      bookTitle.includes(query) ||
      isbn.includes(query) ||
      userName.includes(query) ||
      cardNumber.includes(query)
    );
  });

  //true false count
  const trueCount = filteredIssuedBooks.filter(
    issue => issue.is_book_bank === true || issue.is_book_bank === 1
  ).length;

  const falseCount = filteredIssuedBooks.filter(
    issue => !(issue.is_book_bank === true || issue.is_book_bank === 1)
  ).length;

  console.log("true count", trueCount)
  console.log("false count", falseCount)
  const getDaysRemaining = (dueDate) => {
    if (!dueDate) return null;
    try {
      const due = new Date(dueDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      due.setHours(0, 0, 0, 0);
      const diffTime = due - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch (error) {
      return null;
    }
  };

  const issueColumns = [
    {
      field: "book_title",
      label: "Book Title",
      width: 250,
      render: (value, record) => (
        <strong
          style={{
            color: "var(--primary-color)",
            cursor: 'pointer',
            textDecoration: 'none',
            fontWeight: '500'
          }}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/book/${record.books_id}`, { state: { "call": "bookIssue" } });
          }}
        >
          {value || "N/A"}
        </strong>
      ),
    },
    {
      field: "issued_to_name",
      label: "Issued To",
      width: 200,
      render: (value, record) => {
        const displayName =
          record.issued_to_name ||
          record.member_name ||
          record.student_name ||
          record.user_name ||
          record.name ||
          `${record.first_name || ''} ${record.last_name || ''}`.trim() ||
          "N/A";

        const userId = record.issued_to || record.card_id;

        if (userId && displayName !== "N/A") {
          return (
            <a
              href={`/librarycard/${userId}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/librarycard/${userId}`, { state: record });
              }}
              style={{
                color: "var(--primary-color)",
                textDecoration: "none",
                fontWeight: 500,
              }}
              onMouseEnter={(e) => e.target.style.textDecoration = "underline"}
              onMouseLeave={(e) => e.target.style.textDecoration = "none"}
            >
              {displayName}
            </a>
          );
        }
        return displayName;
      },
    },
    {
      field: "issued_by",
      label: "Issued By",
      width: 200,
      render: (value, record) => {
        const userId = record.issued_by;
        const displayName = record.issued_by_name || "N/A";

        if (userId) {
          return (
            <a
              href={`/user/${userId}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                try {
                  sessionStorage.setItem(
                    `prefetch:user:${userId}`,
                    JSON.stringify(record)
                  );
                } catch (err) { }
                navigate(`/user/${userId}`, { state: record });
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
                try {
                  sessionStorage.setItem(
                    `prefetch:user:${userId}`,
                    JSON.stringify(record)
                  );
                } catch (err) { }
                window.open(`/user/${userId}`, "_blank");
              }}
              style={{
                color: "var(--primary-color)",
                textDecoration: "none",
                fontWeight: 500,
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.target.style.textDecoration = "underline";
              }}
              onMouseLeave={(e) => {
                e.target.style.textDecoration = "none";
              }}
              title="Click to view user details (Right-click to open in new tab)"
            >
              {displayName}
            </a>
          );
        }
        return displayName;
      },
    },
    {
      field: "book_callnumber",
      label: `${callNumberLabel}`,
      width: 150,
      render: (value) => value || "-",
    },
    {
      field: "book_barcode",
      label: "Barcode",
      width: 150,
      render: (value) => value || "-",
    },
    {
      field: "card_number",
      label: "Card Number",
      width: 150,
      render: (value) => value || "-",
    },
    {
      field: "issue_date",
      label: "Issue Date",
      width: 120,
      render: (value) => {
        return moment(convertToUserTimezone(value, timeZone)).format('l')
      }
    },
    {
      field: "due_date",
      label: "Submission Date",
      width: 180,
      render: (value, record) => {
        if (!value) return "—";
        const displayDate = moment(convertToUserTimezone(value, timeZone)).format('l');
        return (
          <div>
            <div style={{ fontWeight: 500 }}>{displayDate}</div>
          </div>
        );
      },
    },
    {
      field: "status",
      label: "Status",
      render: (value) => (
        <Badge
          style={{
            borderRadius: "10px",
            padding: "5px 30px 5px 25px",
          }}
          bg={value === "submitted" ? "secondary" : "primary"}>
          {value === "submitted" ? "Submitted" : "Issued"}
        </Badge>
      ),
    },
    // Add this column to show book bank status
    // {
    //   field: "is_book_bank",
    //   label: "Book Bank",
    //   width: 100,
    //   render: (value) => (
    //     <Badge
    //       bg={value ? "info" : "secondary"}
    //       style={{ fontSize: "0.75rem", padding: "5px 10px" }}
    //     >
    //       {value ? "Yes" : "No"}
    //     </Badge>
    //   ),
    // },
  ];


  if (permissions.loading) {
    return <div className="loading"></div>;
  }

  if (!permissions.allowCreate || !permissions.allowView) {
    return <PermissionDenied />;
  }

  // Calculate counts for display
  const bookBankCount = issuedBooks.filter(issue => issue.is_book_bank === true).length;
  const regularCount = issuedBooks.filter(issue => issue.is_book_bank !== true).length;

  const FetchCompany = async () => {
    try {
      const tenantCode = getTenantCodeFromToken();

      if (!tenantCode) {
        console.error("Tenant Code not found in token");
        return;
      }

      const api = new DataApi("company");
      const resp = await api.fetchAll();

      let companies = [];

      if (resp?.data?.success) {
        companies = resp.data.data || [];
      } else if (Array.isArray(resp?.data)) {
        companies = resp.data;
      }

      // tenant_code match karo
      const company = companies.find(
        (item) => item.tenantcode === tenantCode
      );

      if (!company) {
        console.error("Company not found for tenant:", tenantCode);
        return;
      }

      const bookBankMode =
        company?.book_bank === true || company?.book_bank === 1;

      setCompany(company);
      setCompanybookbank(bookBankMode);
      setBookBankLimit(parseInt(company?.book_bank_books_limit) || 0);

      console.log("Matched Company:", company);
      console.log("Book Bank Mode:", bookBankMode);
    } catch (e) {
      console.error("Error loading company settings:", e);
    }
  };
  function getTenantCodeFromToken() {
    const token = sessionStorage.getItem("token");
    if (!token) return null;

    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.tenantcode || payload.tenantcode || null;
  }
  return (
    <Card style={{ border: "1px solid #e2e8f0", boxShadow: "none", borderRadius: "8px", overflow: "hidden" }}>
      <Card.Body>
        <Tab.Container
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k || "issue")}
          id="book-tabs-container"
        >
          <div className="d-flex align-items-center justify-content-between border-bottom pb-2">
            <div className="d-flex align-items-center">
              <Nav variant="tabs" className="border-bottom-0">
                <Nav.Item>
                  <Nav.Link
                    eventKey="issue"
                    className={`fw-semibold ${activeTab === 'issue' ? 'active' : ''}`}
                    style={{
                      border: "none",
                      borderRadius: "8px 8px 0 0",
                      padding: "12px 24px",
                      backgroundColor: activeTab === 'issue' ? "var(--primary-color)" : "#f8f9fa",
                      color: activeTab === 'issue' ? "white" : "#64748b",
                      borderTop: activeTab === 'issue' ? "3px solid var(--primary-color)" : "3px solid transparent",
                      fontSize: "14px",
                      transition: "all 0.3s ease",
                      marginBottom: "-1px"
                    }}
                  >
                    <span>Issue New Book</span>
                  </Nav.Link>
                </Nav.Item>

                <Nav.Item>
                  <Nav.Link
                    eventKey="list"
                    className={`fw-semibold ${activeTab === 'list' ? 'active' : ''}`}
                    style={{
                      border: "none",
                      borderRadius: "8px 8px 0 0",
                      padding: "12px 24px",
                      backgroundColor: activeTab === 'list' ? "var(--primary-color)" : "#f8f9fa",
                      color: activeTab === 'list' ? "white" : "#64748b",
                      borderTop: activeTab === 'list' ? "3px solid var(--primary-color)" : "3px solid transparent",
                      fontSize: "14px",
                      transition: "all 0.3s ease",
                      marginBottom: "-1px"
                    }}
                  >
                    {/* <span>View Issued Books ({bookBankMode ?trueCount:falseCount})</span> */}
                    <span>View Issued Books </span>
                  </Nav.Link>
                </Nav.Item>
              </Nav>

              {/* Move toggle here, next to tabs */}
              {activeTab === "list" && companybookbank && (
                <Form.Check
                  style={{ marginLeft: "30px", marginTop: "0" }}
                  type="switch"
                  id="book-bank-mode-switch"
                  label={
                    <div className="d-flex align-items-center">
                      <span className="fw-bold me-2">Book Bank Mode</span>
                    </div>
                  }
                  checked={bookBankMode}
                  onChange={(e) => setBookBankMode(e.target.checked)}
                  className="mb-0"
                />
              )}
            </div>

            {activeTab === "list" && (
              <div className="d-flex align-items-center">
                <InputGroup style={{ width: "250px", maxWidth: "100%" }}>
                  <InputGroup.Text
                    style={{
                      background: "var(--primary-color)",
                      borderColor: "#e9ecef",
                      padding: "0.375rem 0.75rem",
                      borderRight: "none",
                      borderTopLeftRadius: "6px",
                      borderBottomLeftRadius: "6px"
                    }}
                  >
                    <i
                      className="fa-solid fa-search"
                      style={{ color: "white", fontSize: "0.875rem" }}
                    ></i>
                  </InputGroup.Text>

                  <Form.Control
                    placeholder="Search books..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      borderColor: "#e9ecef",
                      fontSize: "0.875rem",
                      padding: "0.375rem 0.75rem",
                      borderLeft: "none",
                      borderRight: searchTerm ? "none" : "1px solid #e9ecef"
                    }}
                  />

                  {searchTerm && (
                    <Button
                      variant="outline-secondary"
                      onClick={() => setSearchTerm("")}
                      style={{
                        border: "1px solid #d1d5db",
                        borderLeft: "none",
                        height: "38px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0 12px",
                        borderTopRightRadius: "6px",
                        borderBottomRightRadius: "6px"
                      }}
                    >
                      <i className="fa-solid fa-times"></i>
                    </Button>
                  )}
                </InputGroup>
              </div>
            )}
          </div>

          <Tab.Content className="pt-3">
            <Tab.Pane eventKey="issue">
              <BulkIssue fetchIssuedBooks={fetchIssuedBooks} />
            </Tab.Pane>

            <Tab.Pane eventKey="list">
              <Card className="shadow-sm border-0">
                <Card.Body className="p-0" style={{ overflow: "hidden" }}>
                  <ResizableTable
                    data={filteredIssuedBooks}
                    columns={issueColumns}
                    loading={loadingIssuedBooks}
                    showCheckbox={false}
                    showSerialNumber={true}
                    showActions={false}
                    searchTerm={searchTerm}
                    currentPage={currentPage}
                    recordsPerPage={recordsPerPage}
                    onPageChange={(page) => {
                      setCurrentPage(page);
                    }}
                    // emptyMessage={
                    //   searchTerm
                    //     ? `No ${bookBankMode ? 'book bank' : 'regular'} issued books found matching your search`
                    //     : `No ${bookBankMode ? 'book bank' : 'regular'} books have been issued yet`
                    // }
                    onRowClick={(issue) => {
                      console.log("Issue clicked:", issue);
                    }}
                  />
                </Card.Body>
                {filteredIssuedBooks.length > 0 && (
                  <Card.Footer
                    style={{
                      background: "#f8f9fa",
                      borderTop: "1px solid #e9ecef",
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      {/* <div>
                        <strong>Total {bookBankMode ? 'Book Bank' : 'Regular'} Issued Books:</strong>{" "}
                        {filteredIssuedBooks.length}
                        {searchTerm && (
                          <span className="text-muted ms-2">
                            (Filtered from {bookBankMode ? bookBankCount : regularCount} total)
                          </span>
                        )}
                      </div> */}
                      {/* <div>
                        <Badge bg="secondary" className="me-2">
                          Regular: {regularCount}
                        </Badge>
                        <Badge bg="info">
                          Book Bank: {bookBankCount}
                        </Badge>
                      </div> */}
                    </div>
                  </Card.Footer>
                )}
              </Card>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Card.Body>
    </Card>
  );
};

export default BookIssue;